import React from 'react';
import { Input } from './ui/input';

/**
 * Числовой input с независимым текстовым буфером.
 *
 * Родитель хранит числовое значение (например 0), но пользователь может
 * очистить поле и видеть ПУСТО, а не «0». Это удобно, когда нужно ввести
 * новое значение вместо старого — не приходится сначала стирать «0».
 *
 * onChange(number) вызывается на каждое нажатие клавиши; пустое поле
 * передаётся как 0 (для сохранения в БД), при этом визуально остаётся пусто.
 */
export function NumberInput({ value, onChange, onBlur, min = 0, className = '', ...rest }) {
  const [text, setText] = React.useState(() => (value == null ? '' : String(value)));
  const lastNumericRef = React.useRef(value ?? 0);
  const isFocusedRef = React.useRef(false);

  // Синхронизация с внешним значением: только если извне пришло ДРУГОЕ число
  // (т.е. не наше собственное onChange). Если пользователь сейчас редактирует
  // и стёр поле — не перезаписываем пустоту.
  React.useEffect(() => {
    const external = value ?? 0;
    if (external === lastNumericRef.current) return;
    lastNumericRef.current = external;
    if (!isFocusedRef.current) {
      setText(value == null ? '' : String(value));
    }
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    setText(raw);
    const parsed = raw === '' ? 0 : Math.max(min, parseInt(raw, 10) || 0);
    lastNumericRef.current = parsed;
    onChange && onChange(parsed);
  };

  const handleFocus = () => { isFocusedRef.current = true; };
  const handleBlur = (e) => {
    isFocusedRef.current = false;
    onBlur && onBlur(e);
  };

  return (
    <Input
      {...rest}
      type="number"
      inputMode="numeric"
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={className}
    />
  );
}

export default NumberInput;
